import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
} from '@angular/core';
import {
  AxsButtonComponent,
  AxsGalleryComponent,
  AxsGalleryItemComponent,
} from '@vanguard/axs-angular';
import { ButtonSize, ButtonTheme } from '@vanguard/axs-core';
import { ContentService } from 'src/app/shared/services/content/content.service';
import { content } from 'src/assets/content/fallback-content';

import { AdobeLaunchService } from '../../adobe-launch/adobe-launch.service';
import { SharedInfoService } from '../../shared/services/shared-info/shared-info.service';
import {
  ModalData,
  SampleClientModalComponent,
} from './sample-client-modal/sample-client-modal.component';

@Component({
  selector: 'sample-clients',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    AxsGalleryComponent,
    AxsGalleryItemComponent,
    AxsButtonComponent,
    SampleClientModalComponent,
  ],
  templateUrl: './sample-clients.component.html',
  styleUrl: './sample-clients.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SampleClientsComponent implements OnInit, AfterViewInit {
  @Output() clientSelected = new EventEmitter<void>();
  sampleClientsContent: any = content.Data.v4_rcc_root.content.sampleClientsSection;

  useCustomControls = true;
  numberOfShownItems = 2;
  galleryActiveItem = 0;
  activeClientIndex = 0;
  sampleClientData: ClientData[] = [];
  gallerySlotData: any[] = [];

  get galleryMultiLayout() {
    return this.numberOfShownItems > 1;
  }

  get paginationIndices(): number[] {
    const totalPages = Math.ceil(this.sampleClientData.length / this.numberOfShownItems);
    return Array.from({ length: totalPages }, (_, i) => i * this.numberOfShownItems);
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonTheme = ButtonTheme;

  isSampleClientModalOpen = false;
  modalData: ModalData;

  constructor(
    private readonly contentService: ContentService,
    private sharedInfoService: SharedInfoService,
    private readonly adobeLaunchService: AdobeLaunchService,
  ) {}

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.numberOfShownItems = window.innerWidth < 768 ? 1 : 2;
    this.synchronizeCardHeights();
  }

  ngOnInit(): void {
    this.onResize();
    this.setupGalleryData();
    this.contentService.getContent().subscribe((data) => {
      this.sampleClientsContent = data.Data.v4_rcc_root.content.sampleClientsSection;
      // The gallery cards and modals are built out of the content, so rebuild them with it.
      this.setupGalleryData();
    });
    this.sharedInfoService.setSampleClientIndex(1); // TODO: remove after 'defaultClientInfo' can be removed with V4 launch
    this.sharedInfoService.getSampleClientModified$().subscribe((modified) => {
      if (modified) {
        this.activeClientIndex = -1;
      }
    });
  }

  ngAfterViewInit(): void {
    this.tryUpdateCardHeights(0);
  }

  private tryUpdateCardHeights(attempt: number): void {
    const cards = document.querySelectorAll('.sample-client-card__container');
    if (cards.length > 0) {
      this.synchronizeCardHeights();
    } else if (attempt < 10) {
      // Try again after a short delay, up to 10 attempts (total ~1 second)
      setTimeout(() => this.tryUpdateCardHeights(attempt + 1), 100);
    }
  }

  synchronizeCardHeights(): void {
    const cards = document.querySelectorAll('.sample-client-card__container');
    if (cards.length === 0) return;

    cards.forEach((card: Element) => {
      (card as HTMLElement).style.minHeight = 'auto';
    });

    let maxHeight = 0;
    cards.forEach((card: Element) => {
      const height = (card as HTMLElement).offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });

    if (maxHeight > 0) {
      cards.forEach((card: Element) => {
        (card as HTMLElement).style.minHeight = `${maxHeight}px`;
      });
    }
  }

  setupGalleryData(): void {
    const sampleClientCards = this.sampleClientsContent?.sampleClientCards || [];
    const sampleClientModals = this.sampleClientsContent?.sampleClientModals?.data || [];

    const localSampleClientCardImages = [
      'assets/images/sample-client-card/paul.svg',
      'assets/images/sample-client-card/fiona.svg',
      'assets/images/sample-client-card/steady-saver.svg',
      'assets/images/sample-client-card/legacy-planners.svg',
    ];

    const localSampleClientModalImages = [
      'assets/images/sample-client-modal/paul.svg',
      'assets/images/sample-client-modal/fiona.svg',
      'assets/images/sample-client-modal/steady-saver.svg',
      'assets/images/sample-client-modal/legacy-planners.svg',
    ];

    this.sampleClientData = sampleClientCards.map((card: any, index: number): ClientData => {
      const cardImgPath = card.imgPath || localSampleClientCardImages[index];
      return {
        imgPath: cardImgPath,
        cardText: card.cardText,
        modalData: {
          index: index,
          title: sampleClientModals[index]?.title,
          name: sampleClientModals[index]?.name,
          clientProfile: sampleClientModals[index]?.clientProfile,
          conversionReasoning: sampleClientModals[index]?.conversionReasoning,
          calculatorExplanation: sampleClientModals[index]?.calculatorExplanation,
          imgPath: sampleClientModals[index]?.imgPath || localSampleClientModalImages[index],
          imgPathCard: cardImgPath,
        },
      };
    });

    // Rebuild rather than append — this runs once against the seeded fallback and again when
    // AEM content lands, and appending would duplicate every slot.
    this.gallerySlotData = this.sampleClientData.map((sampleClient, index: number) => ({
      tabAriaLabel: sampleClient.modalData?.title,
      slotName: `slot${index + 1}`,
    }));

    this.modalData = this.sampleClientData[0]?.modalData;
  }

  onPageChange(event: any): void {
    setTimeout(() => {
      this.galleryActiveItem = event.activeItem;
    }, 0);
  }

  // for native gallery control
  onNext(event): void {
    this.galleryActiveItem = event.activeItem + 1;
  }

  // for native gallery control
  onPrev(event): void {
    this.galleryActiveItem = event.activeItem - 1;
  }
  next(): void {
    this.galleryActiveItem =
      (this.galleryActiveItem + this.numberOfShownItems) % this.sampleClientData.length;
    this.adobeLaunchService.captureAction('sample-client-arrow-forward', 'button');
  }

  prev(): void {
    this.galleryActiveItem =
      (this.galleryActiveItem - this.numberOfShownItems + this.sampleClientData.length) %
      this.sampleClientData.length;
    this.adobeLaunchService.captureAction('sample-client-arrow-backward', 'button');
  }

  paginationClick(event: any): void {
    this.galleryActiveItem = event.index;
  }

  useSampleClient(index: number): void {
    this.activeClientIndex = index;
    const clientName = this.sampleClientData[index]?.modalData?.title?.toLowerCase();
    this.adobeLaunchService.captureAction(
      `sample-client calculate:${clientName},position:${index + 1}`,
      'button',
    );
    this.sharedInfoService.setSampleClientIndex(this.activeClientIndex + 1); // TODO: remove `+1` after 'defaultClientInfo' can be removed with V4 launch
    this.clientSelected.emit();
  }

  openSampleClientModal(modalData: ModalData): void {
    const clientName = modalData?.title?.toLowerCase();
    const position = modalData?.index + 1;
    this.adobeLaunchService.captureAction(
      `sample-client details:${clientName},position:${position}`,
      'button',
    );
    this.modalData = modalData;
    this.isSampleClientModalOpen = true;
  }

  handleLoadClient(index: number): void {
    this.useSampleClient(index);
  }
}

export interface ClientData {
  imgPath: string;
  cardText: string;
  modalData: ModalData;
}
